import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export type FcrCoreMonitorLine = {
  ts: string;
  line: string;
};

const STREAM_MAX = 200;
const EVENTS_MAX = 100;

function asLines(raw: unknown, max: number): FcrCoreMonitorLine[] {
  if (!Array.isArray(raw)) return [];
  const out: FcrCoreMonitorLine[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as { ts?: unknown; line?: unknown };
    out.push({
      ts: String(row.ts || ""),
      line: String(row.line || ""),
    });
  }
  return out.slice(-max);
}

function appendLine(prev: FcrCoreMonitorLine[], item: FcrCoreMonitorLine, max: number) {
  return [...prev, item].slice(-max);
}

export function useFcrCoreMonitor(open = false) {
  const location = useLocation();
  const enabled = open && location.pathname === "/fcr";
  const [stream, setStream] = useState<FcrCoreMonitorLine[]>([]);
  const [events, setEvents] = useState<FcrCoreMonitorLine[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    const es = new EventSource("/api/fcr-core/monitor");
    setError(null);

    es.addEventListener("snapshot", (ev) => {
      try {
        const body = JSON.parse((ev as MessageEvent).data) as {
          stream?: unknown;
          events?: unknown;
        };
        setStream(asLines(body.stream, STREAM_MAX));
        setEvents(asLines(body.events, EVENTS_MAX));
        setConnected(true);
        setError(null);
      } catch {
        setError("invalid monitor snapshot");
      }
    });

    es.addEventListener("stream", (ev) => {
      try {
        const item = JSON.parse((ev as MessageEvent).data) as FcrCoreMonitorLine;
        if (!item?.line) return;
        setStream((prev) => appendLine(prev, item, STREAM_MAX));
      } catch {
        /* ignore */
      }
    });

    es.addEventListener("event", (ev) => {
      try {
        const item = JSON.parse((ev as MessageEvent).data) as FcrCoreMonitorLine;
        if (!item?.line) return;
        setEvents((prev) => appendLine(prev, item, EVENTS_MAX));
      } catch {
        /* ignore */
      }
    });

    es.onerror = () => {
      if (es.readyState === EventSource.CLOSED) {
        setConnected(false);
        setError((prev) => prev ?? "monitor stream closed");
      }
    };

    return () => {
      es.close();
      setConnected(false);
    };
  }, [enabled, location.key]);

  return { stream, events, connected, error };
}
